<template>
    <div>
        <g-row class="g-mt-8">
            <g-column :span="24" class="g-mb-6">
                <g-typography tag="h2" font-weight="semibold" text-align="center">{{ title }}</g-typography>
            </g-column>
        </g-row>

        <g-row v-if="!editMode()" class="g-mb-6">
            <g-column :span="24">
                <img :src="PAYOUT_METHOD_ADD_BANK_ACCOUNT_IMAGE" width="100%" />
            </g-column>
        </g-row>

        <g-row type="flex" align="middle" justify="space-around" class="g-mb-7">
            <g-column :span="24">
                <g-form class="g-pb-0 g-mb-5">
                    <g-form-item label="Name on Account" class="g-mb-3">
                        <g-input v-model="accountName" type="text" :required="!editMode()" :disabled="editMode()" />
                    </g-form-item>

                    <g-row>
                        <g-column :span="24">
                            <g-form-item style="padding: 0" label="Account Type">
                                <g-radio-group v-model="accountType" :disabled="editMode()">
                                    <g-radio value="company" size="large">
                                        <g-typography>Business</g-typography>
                                    </g-radio>

                                    <g-radio value="individual" size="large">
                                        <g-typography>Individual</g-typography>
                                    </g-radio>
                                </g-radio-group>
                            </g-form-item>
                        </g-column>
                    </g-row>

                    <g-row type="flex" :gutter="16" class="xs-break">
                        <g-column :span="12">
                            <g-form-item label="Routing Number" class="g-mb-0 g-pb-0">
                                <g-input
                                    v-model="routingNumber"
                                    :max-length="FORM_ACH_ROUTING_NUMBER_MAX_LENGTH"
                                    :disabled="editMode()"
                                />
                            </g-form-item>
                        </g-column>

                        <g-column :span="12">
                            <g-form-item label="Bank Account Number" class="g-mb-0 g-pb-0">
                                <g-input
                                    v-model="accountNumber"
                                    :max-length="FORM_ACH_ACCOUNT_NUMBER_MAX_LENGTH"
                                    :disabled="editMode()"
                                />
                            </g-form-item>
                        </g-column>
                    </g-row>

                    <g-row type="flex" :gutter="16" class="g-mt-4 xs-break">
                        <g-column :span="12">
                            <g-typography tag="h4" font-weight="label" class="g-mb-3">
                                Country
                                <g-tooltip
                                    class="tooltip"
                                    placement="top"
                                    title="List of countries we currently support."
                                >
                                    <g-icon size="small" class="g-text-grey-5 g-mr-3" name="question-circle" />
                                </g-tooltip>
                            </g-typography>

                            <g-select
                                v-model="selectedCountry"
                                class="country-select"
                                show-search
                                option-filter-prop="children"
                                :disabled="editMode()"
                                @change="updateCurrencies"
                            >
                                <g-select-option
                                    v-for="(country, code) in PAYOUTS_COUNTRY_CURRENCIES"
                                    :key="code"
                                    :value="code"
                                >
                                    {{ country.name }}
                                </g-select-option>
                            </g-select>
                        </g-column>

                        <g-column :span="12">
                            <g-typography tag="h4" font-weight="label" class="g-mb-3">
                                Account Currency
                                <g-tooltip
                                    class="tooltip"
                                    placement="top"
                                    title="List of currencies we currently support."
                                >
                                    <g-icon size="small" class="g-text-grey-5 g-mr-3" name="question-circle" />
                                </g-tooltip>
                            </g-typography>

                            <g-select v-model="selectedCurrency" placeholder="Select" :disabled="editMode()">
                                <g-select-option v-for="(currency, i) in currencies" :key="i" :value="currency.code">
                                    {{ currency.name }}
                                </g-select-option>
                            </g-select>
                        </g-column>
                    </g-row>

                    <g-row>
                        <g-column>
                            <hr />
                        </g-column>
                    </g-row>

                    <g-row type="flex" justify="space-between" class="g-mb-5">
                        <g-column>
                            <g-typography tag="h4" font-weight="label">
                                {{ DEFAULT_PAYOUT_METHOD_LABEL_STRING }}
                            </g-typography>
                        </g-column>

                        <g-column>
                            <g-switch v-model="isDefault" :disabled="firstPayoutMethod && !editMode()">
                                <g-icon slot="checkedChildren" name="check" size="xsmall" />
                                <g-icon slot="unCheckedChildren" name="times" size="xsmall" />
                            </g-switch>
                        </g-column>
                    </g-row>

                    <g-row>
                        <g-column>
                            <g-typography tag="p" class="g-mb-3">
                                {{ DEFAULT_PAYOUT_METHOD_LINE_1_STRING }}
                            </g-typography>

                            <g-typography tag="p">
                                {{ DEFAULT_PAYOUT_METHOD_LINE_2_STRING }}
                            </g-typography>
                        </g-column>
                    </g-row>
                </g-form>
            </g-column>
        </g-row>

        <g-row type="flex" align="middle" justify="space-around" class="g-mb-4">
            <g-column>
                <g-button
                    shape="round"
                    theme="live"
                    type="primary"
                    size="large"
                    :loading="isSubmitting"
                    :disabled="!isReady() || isSubmitting"
                    @click="handleSubmit"
                >
                    {{ primaryButtonText }}
                </g-button>
            </g-column>
        </g-row>

        <g-row type="flex" align="middle" justify="space-around" class="g-mb-4">
            <g-column>
                <g-button theme="live" :border="false" :shadow="false" @click="handleSecondaryAction">
                    {{ secondaryButtonText }}
                </g-button>
            </g-column>
        </g-row>
    </div>
</template>

<script>
    import { ref, toRefs, watch, computed, onMounted } from 'vue-demi';
    import { PAYOUTS_COUNTRY_CURRENCIES } from '../../../../../app-v2/constants/payouts-currencies';
    import { PAYOUT_METHOD_ADD_BANK_ACCOUNT_IMAGE } from '../../../../../app-v2/constants/cdn-assets';
    import {
        FORM_ACH_ROUTING_NUMBER_MAX_LENGTH,
        FORM_ACH_ACCOUNT_NUMBER_MAX_LENGTH,
        FORM_ALPHANUMERIC_REGEXP,
        FORM_NUMERIC_REGEXP
    } from '../../../../../app-v2/constants/forms';
    import {
        DEFAULT_PAYOUT_METHOD_LABEL_STRING,
        DEFAULT_PAYOUT_METHOD_LINE_1_STRING,
        DEFAULT_PAYOUT_METHOD_LINE_2_STRING,
        PAYOUT_METHOD_MODE_EDIT
    } from '../../../../../app-v2/constants/payouts';
    import { EVENT_NAME_PAYOUTS_MANUAL_ACH_SUBMITTED } from '../../../../../app-v2/constants/analytics-events';
    import useToggle from '../../../../composables/use-toggle';
    import useMessage from '../../../../composables/use-message';
    import usePayoutMethods from '../../../../composables/use-payout-methods';
    import useEventTracking from '../../../../composables/use-event-tracking';
    import useWhiteLabeling from '../../../../composables/use-white-labeling';
    import useContactStore from '../../../../../stores/contact';

    export default {
        name: 'BankAccountForm',

        props: {
            payoutAccount: {
                type: Object,
                default: null
            },
            payoutMethod: {
                type: Object,
                default: null
            },
            mode: {
                type: String,
                default: ''
            }
        },

        setup(props, context) {
            const events = useEventTracking();
            const contact = useContactStore().data;
            const { message } = useMessage();
            const accountName = ref('');
            const accountType = ref('');
            const routingNumber = ref('');
            const accountNumber = ref('');
            const isDefault = ref(false);
            const firstPayoutMethod = ref(false);

            firstPayoutMethod.value = !props.payoutAccount?.payout_method_id;
            if (firstPayoutMethod.value) {
                isDefault.value = true;
            }

            const editMode = () => {
                return props.mode === PAYOUT_METHOD_MODE_EDIT;
            };

            const title = computed(() => {
                return `${editMode() ? 'Edit' : 'Add'} a Payout Method`;
            });

            const primaryButtonText = computed(() => {
                return editMode() ? 'Update' : 'Add Bank Account';
            });

            const secondaryButtonText = computed(() => {
                return editMode() ? 'Remove' : 'Go Back';
            });

            watch(routingNumber, (newVal, oldVal) => {
                if (!editMode() && !newVal.toString().match(FORM_NUMERIC_REGEXP)) {
                    routingNumber.value = oldVal;
                }
            });

            watch(accountNumber, (newVal, oldVal) => {
                if (!editMode() && !newVal.toString().match(FORM_ALPHANUMERIC_REGEXP)) {
                    accountNumber.value = oldVal;
                }
            });

            /**
             * Currency selection
             */
            const updateCurrencies = () => {
                currencies.value = PAYOUTS_COUNTRY_CURRENCIES[selectedCountry.value]?.currencies;
                selectedCurrency.value =
                    PAYOUTS_COUNTRY_CURRENCIES[selectedCountry.value]?.currencies?.length === 1
                        ? PAYOUTS_COUNTRY_CURRENCIES[selectedCountry.value]?.currencies[0].code
                        : undefined;
            };

            const selectedCountry = ref(props.payoutAccount?.country_code);
            const selectedCurrency = ref('');
            const currencies = ref([]);
            updateCurrencies();

            const prefillForm = () => {
                if (!editMode()) {
                    return;
                }

                accountName.value = props.payoutMethod?.holder_name;
                accountType.value = props.payoutMethod?.holder_type;
                accountNumber.value = routingNumber.value = '••••••••••••';
                selectedCountry.value = props.payoutMethod?.country;
                updateCurrencies();
                selectedCurrency.value = props.payoutMethod?.currency;
                isDefault.value = props.payoutMethod?.id === props.payoutAccount?.payout_method_id;
            };

            onMounted(() => {
                if (editMode()) {
                    prefillForm();
                }
            });

            const { payoutMethod } = toRefs(props);
            watch(payoutMethod, () => {
                prefillForm();
            });

            /**
             * Bank Account Submission
             */
            const isReady = () => {
                if (!editMode()) {
                    if (!accountName.value) {
                        return false;
                    }

                    if (!accountType.value) {
                        return false;
                    }

                    if (!routingNumber.value) {
                        return false;
                    }

                    if (!accountNumber.value) {
                        return false;
                    }

                    if (!selectedCurrency.value) {
                        return false;
                    }
                }

                return true;
            };

            const handleSubmit = () => {
                if (props.mode === PAYOUT_METHOD_MODE_EDIT) {
                    submitUpdateBank();
                } else if (isReady()) {
                    createBankAccount();
                }
            };

            const handleSecondaryAction = () => {
                if (props.mode === PAYOUT_METHOD_MODE_EDIT) {
                    context.emit('remove');
                } else {
                    context.emit('back');
                }
            };

            const { currentValue: isSubmitting, setTrue: submitting, setFalse: doneSubmitting } = useToggle(false);

            const submitUpdateBank = async () => {
                submitting();

                const { editBankAccount: updateBank } = usePayoutMethods();
                updateBank(contact.id, props.payoutMethod?.id, isDefault.value)
                    .then(() => {
                        context.emit('success');
                    })
                    .catch((error) => {
                        message.responseError(error);
                    })
                    .finally(() => {
                        doneSubmitting();
                    });
            };

            const createBankAccount = () => {
                const payload = {
                    holder_name: accountName.value,
                    holder_type: accountType.value,
                    account_number: accountNumber.value,
                    routing_number: routingNumber.value,
                    country: selectedCountry.value,
                    currency: selectedCurrency.value,
                    is_default: isDefault.value
                };

                submitting();

                const { addBankAccount } = usePayoutMethods();
                addBankAccount(context.root.contact.id, payload)
                    .then(() => {
                        events.trackEvent(EVENT_NAME_PAYOUTS_MANUAL_ACH_SUBMITTED, getEventPayload());
                        context.emit('success');
                    })
                    .catch((error) => {
                        message.responseError(error);
                        events.trackEvent(EVENT_NAME_PAYOUTS_MANUAL_ACH_SUBMITTED, getEventPayload(), false);
                    })
                    .finally(() => {
                        doneSubmitting();
                    });
            };

            const getEventPayload = () => {
                return {
                    contact_id: contact.id,
                    is_whitelabled: useWhiteLabeling().isWhiteLabeled(),
                    has_pending_payout: contact.has_pending_payout,
                    has_additional_payout_method: props.payoutAccount?.external_account?.payout_methods.length > 0
                };
            };

            return {
                title,
                primaryButtonText,
                secondaryButtonText,
                handleSecondaryAction,
                editMode,

                DEFAULT_PAYOUT_METHOD_LABEL_STRING,
                DEFAULT_PAYOUT_METHOD_LINE_1_STRING,
                DEFAULT_PAYOUT_METHOD_LINE_2_STRING,
                FORM_ACH_ROUTING_NUMBER_MAX_LENGTH,
                FORM_ACH_ACCOUNT_NUMBER_MAX_LENGTH,
                PAYOUT_METHOD_ADD_BANK_ACCOUNT_IMAGE,
                accountName,
                accountType,
                routingNumber,
                accountNumber,
                isDefault,
                firstPayoutMethod,

                isReady,
                handleSubmit,
                isSubmitting,

                selectedCountry,
                selectedCurrency,
                currencies,
                updateCurrencies,
                PAYOUTS_COUNTRY_CURRENCIES
            };
        }
    };
</script>

<style lang="scss" scoped>
    .ant-btn.theme-live {
        padding: 0 $g-spacing-10 !important;
    }

    hr {
        height: 1px;
        background-color: $g-color-grey-3;
    }

    @media only screen and (max-width: $g-screen-size-xs) {
        .ant-row-flex.xs-break .ant-col {
            width: 100%;
        }
        .country-select {
            margin-bottom: $g-spacing-4;
        }
    }
</style>
